/**
 * Interview Table Input Schema
 *
 * Combined Zod schema for all interview table query parameters.
 * This schema is used by tRPC endpoints and can be serialized to URL params.
 */

import { z } from "zod";

import { filterGroupSchema } from "./filter-schemas";
import { sortConfigSchema } from "./sort-schemas";

// ============================================================================
// Interview Status Enum
// ============================================================================

export const interviewStatusSchema = z.enum([
  "PENDING",
  "ACTIVE",
  "PROCESSING",
  "AWAITING_REVIEW",
  "APPROVED",
  "REJECTED",
  "DISPUTED",
  "RETURNED",
  "TIMED_OUT",
  "FAILED",
]);

export type InterviewStatusFilter = z.infer<typeof interviewStatusSchema>;

// ============================================================================
// Device Type Enum
// ============================================================================

export const deviceTypeSchema = z.enum([
  "MOBILE_IOS",
  "MOBILE_ANDROID",
  "DESKTOP",
]);

export type DeviceTypeFilter = z.infer<typeof deviceTypeSchema>;

// ============================================================================
// Panel Interview Table Input Schema
// ============================================================================

/**
 * Input schema for panel interview table queries.
 * Uses server-side filtering with panelId as the scope.
 */
export const panelInterviewTableInputSchema = z.object({
  // ========================================
  // Scope Filter (required)
  // ========================================

  /**
   * Panel ID for authorization and scoping
   */
  panelId: z.string().min(1),

  /**
   * Optional study ID to filter by specific study within the panel
   */
  studyId: z.string().min(1).optional(),

  // ========================================
  // Advanced Filters (FilterGroup)
  // ========================================

  /**
   * Complex filter group with AND/OR logic.
   */
  filterGroup: filterGroupSchema.optional(),

  // ========================================
  // Search
  // ========================================

  /**
   * Full-text search across participant name, email, location
   */
  search: z.string().max(200).optional(),

  // ========================================
  // Sorting
  // ========================================

  /**
   * Sort configuration.
   */
  sortConfig: sortConfigSchema.optional(),

  // ========================================
  // Pagination
  // ========================================

  /**
   * Page number (1-indexed)
   */
  page: z.number().int().min(1).max(1000).default(1),

  /**
   * Items per page
   */
  limit: z.number().int().min(1).max(100).default(50),
});

export type PanelInterviewTableInput = z.infer<
  typeof panelInterviewTableInputSchema
>;

// ============================================================================
// Interview Table Response Types
// ============================================================================

/**
 * Pagination metadata in response
 */
export const paginationResponseSchema = z.object({
  page: z.number(),
  limit: z.number(),
  totalInterviews: z.number(),
  totalPages: z.number(),
  hasNextPage: z.boolean(),
  hasPreviousPage: z.boolean(),
});

export type PaginationResponse = z.infer<typeof paginationResponseSchema>;

/**
 * Metadata about the query execution
 */
export const queryMetadataSchema = z.object({
  executionTimeMs: z.number(),
  filtersApplied: z.record(z.string(), z.boolean()).optional(),
  sortedBy: z
    .object({
      field: z.string(),
      order: z.enum(["asc", "desc"]),
    })
    .optional(),
});

export type QueryMetadata = z.infer<typeof queryMetadataSchema>;
